import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import { formatDateOnlyDMY } from "helpers";
import { buildObservationPreviewSummary } from "helpers/movementArticleObservations";
import { getOrderClientFieldConfig } from "helpers/orderClientFields";
import { getOrderStatusChipColor, getOrderStatusLabel } from "helpers/orders";
import { Movement, Order } from "types";

type Props = {
  open: boolean;
  loading?: boolean;
  order?: Order | null;
  movement?: Movement | null;
  onClose: () => void;
  title?: string;
};

export default function OrderDetailDialog({
  open,
  loading = false,
  order = null,
  movement = null,
  onClose,
  title,
}: Props) {
  const detailMovement = movement ?? order;
  const isOrderDetail = Boolean(order);
  const isCancelledMovement = Boolean(!isOrderDetail && movement?.deleted_at);
  const clientFieldConfig = getOrderClientFieldConfig(
    detailMovement?.client ?? null,
  );
  const showLote = clientFieldConfig.detail.showLote;
  const showOc = clientFieldConfig.detail.showOc;
  const showFechaVencimiento = clientFieldConfig.detail.showFechaVencimiento;
  const showPeso = clientFieldConfig.detail.showPeso;
  const detailArticles = detailMovement?.articles ?? [];
  const detailColumnsCount =
    6 +
    (showLote ? 1 : 0) +
    (showOc ? 1 : 0) +
    (showFechaVencimiento ? 1 : 0) +
    (showPeso ? 1 : 0);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>
        {title ?? (isOrderDetail ? "Detalle del pedido" : "Detalle del egreso")}
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : !detailMovement ? (
          <Typography variant="body2">
            No se pudo cargar el detalle.
          </Typography>
        ) : (
          <Stack spacing={2}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack spacing={1}>
                {isCancelledMovement && (
                  <Typography
                    variant="body2"
                    display="flex"
                    alignItems="center"
                    gap={1}
                  >
                    <Chip label="Anulado" color="default" variant="outlined" />
                  </Typography>
                )}
                <Typography variant="body2">
                  <strong>{isOrderDetail ? "Nro Pedido" : "Nro Egreso"}:</strong>{" "}
                  {detailMovement.id}
                </Typography>
                <Typography variant="body2">
                  <strong>Cliente:</strong>{" "}
                  {detailMovement.client
                    ? `${detailMovement.client.code} - ${detailMovement.client.razon_social}`
                    : "-"}
                </Typography>
                <Typography variant="body2">
                  <strong>Cliente Asociado:</strong>{" "}
                  {isOrderDetail
                    ? order?.movement_order?.associated_client?.razon_social ?? "-"
                    : detailMovement.associated_client ?? "-"}
                </Typography>
                <Typography variant="body2">
                  <strong>Doc. Cliente:</strong>{" "}
                  {detailMovement.client_document?.trim() || "-"}
                </Typography>
                <Typography variant="body2">
                  <strong>Remito:</strong> {detailMovement.numero_remito || "-"}
                </Typography>
                {isOrderDetail && order?.discharge_movement_id && (
                  <Typography variant="body2">
                    <strong>Egreso relacionado:</strong> #
                    {order.discharge_movement_id}
                  </Typography>
                )}
                {isOrderDetail ? (
                  <>
                    <Typography variant="body2">
                      <strong>Fecha Pedido:</strong>{" "}
                      {formatDateOnlyDMY(order?.movement_order?.received_at) || "-"}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Fecha Preparacion:</strong>{" "}
                      {formatDateOnlyDMY(order?.movement_order?.preparation_date) ||
                        "-"}
                    </Typography>
                    <Typography
                      variant="body2"
                      display="flex"
                      alignItems="center"
                      gap={1}
                    >
                      <strong>Estado:</strong>
                      <Chip
                        label={getOrderStatusLabel(
                          order?.movement_order?.order_status,
                        )}
                        color={getOrderStatusChipColor(
                          order?.movement_order?.order_status,
                        )}
                        size="small"
                      />
                    </Typography>
                    {order?.movement_order?.stopped_reason && (
                      <Typography variant="body2">
                        <strong>Motivo detencion:</strong>{" "}
                        {order.movement_order.stopped_reason}
                      </Typography>
                    )}
                    {order?.movement_order?.cancel_reason && (
                      <Typography variant="body2">
                        <strong>Motivo cancelacion:</strong>{" "}
                        {order.movement_order.cancel_reason}
                      </Typography>
                    )}
                  </>
                ) : (
                  <Typography variant="body2">
                    <strong>Fecha Egreso:</strong>{" "}
                    {formatDateOnlyDMY(detailMovement.date) || "-"}
                  </Typography>
                )}
              </Stack>
            </Paper>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Codigo</TableCell>
                    <TableCell>Descripcion</TableCell>
                    {showLote && <TableCell>Lote</TableCell>}
                    {showOc && <TableCell>OC</TableCell>}
                    <TableCell align="right">Cantidad</TableCell>
                    <TableCell align="right">Cant. x Cont.</TableCell>
                    <TableCell align="right">Subtotal</TableCell>
                    {showPeso && <TableCell align="right">Peso</TableCell>}
                    {showFechaVencimiento && <TableCell>Fecha Vencimiento</TableCell>}
                    <TableCell>Ubicacion</TableCell>
                    <TableCell>Observaciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detailArticles.map((article) => (
                    <TableRow key={String(article.id)}>
                      <TableCell>{article.code ?? "-"}</TableCell>
                      <TableCell>{article.description ?? "-"}</TableCell>
                      {showLote && <TableCell>{article.lote ?? "-"}</TableCell>}
                      {showOc && <TableCell>{article.oc ?? "-"}</TableCell>}
                      <TableCell align="right">{article.amount ?? 0}</TableCell>
                      <TableCell align="right">
                        {article.amount_per_container ?? 0}
                      </TableCell>
                      <TableCell align="right">{article.subtotal ?? 0}</TableCell>
                      {showPeso && (
                        <TableCell align="right">{article.subtotal_kg ?? 0}</TableCell>
                      )}
                      {showFechaVencimiento && (
                        <TableCell>
                          {formatDateOnlyDMY(article.fecha_vencimiento ?? null) || "-"}
                        </TableCell>
                      )}
                      <TableCell>
                        {article.article_locations?.location?.code ?? "-"}
                      </TableCell>
                      <TableCell>
                        {buildObservationPreviewSummary(
                          article.observation_preview_units ?? [],
                        ) ?? "-"}
                      </TableCell>
                    </TableRow>
                  ))}

                  {detailArticles.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={detailColumnsCount}>
                        No hay articulos para mostrar.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
